<template>
  <el-dialog
    :title="title || $t('SeaExport.AiDocRecognition')"
    :visible.sync="innerVisible"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    width="678px"
    class="ai-recognition-dialog"
  >
    <div class="upload-area">
      <p v-show="showTip" class="support-tip" style="text-align:left;font-size: 16px">{{
        $t('SeaExport.SupportMultipleFiles')
      }}</p>
      <!-- 初始上传界面 -->
      <div v-if="step === 1" class="drop-area">
        <div class="drop-info">
          <el-upload
            ref="upload"
            class="el-upload-body"
            action="string"
            :before-upload="beforeUpload"
            :http-request="p=>step = 2"
            :show-file-list="false"
            :multiple="false"
            drag
            accept=".pdf,.jpg,.jpeg,.png,.txt,.xls,.xlsx,.doc, .docx"
          >
            <img src="@/assets/icons/ai-recognition-folder.png" class="drop-icon-large" alt="">
            <div class="el-upload__text">
              {{ $t('SeaExport.DragOrClick') }}
              <em>{{ $t('SeaExport.ClickUpload') }}</em>
            </div>
            <div slot="tip" class="el-upload__tip">
              {{ $t('SeaExport.FileSizeLimit') }}
            </div>
          </el-upload>
        </div>
      </div>

      <!-- 文件预览界面 -->
      <div v-if="step === 2" class="preview-area">
        <div class="preview-info">
          <img src="@/assets/icons/ai-recognition-pdf.png" class="drop-icon-large" alt="">
          <span class="file-name">{{ currentFile.name }}</span>
          <el-button class="button-reupload-text" type="text" @click="resetData">{{
            $t('SeaExport.Reupload')
          }}
          </el-button>
        </div>
      </div>

      <!-- 上传进度界面 -->
      <div v-if="step === 3" class="progress-area">
        <div class="progress-info">
          <img src="@/assets/icons/ai-recognition-pdf.png" class="progress-icon-large" alt="">
          <p class="progress-tip">{{ $t('SeaExport.RecognitionInProgress') }}</p>
          <el-progress
            class="progress-bar"
            type="line"
            :show-text="false"
            :percentage="uploadProgress"
          />
        </div>
      </div>

      <!-- 文字识别界面 -->
      <div v-if="step === 4" class="recognition-area">
        <div class="recognition-info">
          <el-input
            ref="recognitionContent"
            v-model="textContent"
            type="textarea"
            :placeholder="placeholder || $t('SeaExport.RecognitionTextTip')"
          />
        </div>
      </div>

      <div v-show="showTip" class="ai-recognition-warn">
        <img src="@/assets/icons/ai-recognition-bell.png" alt="">
        <p>{{ $t('SeaExport.AiRecognitionWarn') }}</p>
      </div>
    </div>

    <span v-if="step === 2 || step === 4" slot="footer" class="button-dialog-footer">
      <el-button
        size="medium"
        :plain="true"
        style="width: 110px; height: 40px; margin-right: 19px;"
        @click="innerVisible = false"
      >{{ $t('SeaExport.Cancel') }}</el-button>
      <el-button size="medium" type="primary" style="width: 110px; height: 40px" @click="startRecognitionHandler">{{
        $t('SeaExport.StartRecognition')
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>

import {
  analyseText,
  getAnalysis,
  importAndAnalyseFile,
  syncOcrByTemplateUuidWithFile
} from '@/api/seaExport/intelligentBooking'
import { getLoading4Cxt } from '@/utils/utilBaseElement'

export default {
  name: 'CargoAiRecognitionDialog',
  props: {
    placeholder: {
      type: String,
      default: '',
      required: false
    },
    title: {
      type: String,
      default: '',
      required: false
    },
    visible: {
      type: Boolean,
      default: false,
      required: true
    },
    multimodal: {
      type: Boolean,
      default: true,
      required: true
    },
    jobUuid: {
      type: String,
      default: '',
      required: false
    },
    docUuid: {
      type: String,
      default: '',
      required: false
    },
    hblNbr2: {
      type: String,
      default: '',
      required: false
    },
    source: {
      type: Number,
      default: 4,
      required: false
    },
    pageSource: {
      type: String,
      default: '',
      required: false
    },
    iChannel: {
      type: Number,
      default: 1,
      required: false
    },
    promptKey: {
      type: String,
      default: 'ADDRESS',
      required: false
    },
    callBack: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      step: 4,
      currentFile: {},
      uploadProgress: 0,
      innerVisible: this.visible,
      textContent: '',
      pollTimer: null, // 添加轮询定时器引用
      progressTimer: null // 添加进度条定时器引用
    }
  },
  computed: {
    showTip() {
      return this.step !== 4
    }
  },
  watch: {
    // 监听 prop 变化
    visible(val) {
      this.innerVisible = val
      if (val) {
        this.currentFile = {}
        this.uploadProgress = 0
        this.textContent = ''
        this.step = this.multimodal ? 1 : 4
      }
      if (!val) {
        this.resetData()
      }
    },
    // 监听内部状态变化
    innerVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    beforeUpload(file) {
      const isValidType = ['.pdf', '.jpg', '.jpeg', '.png', '.txt', '.xls', '.xlsx', '.doc', '.docx'].some(type =>
        file.name.toLowerCase().endsWith(type)
      )
      const isLt4M = file.size / 1024 / 1024 < 4

      if (!isValidType) {
        this.$message.error(this.$t('SeaExport.InvalidFileType'))
      }
      if (!isLt4M) {
        this.$message.error(this.$t('SeaExport.FileTooLarge'))
      }
      this.currentFile = file
    },
    startRecognitionHandler() {
      if (this.multimodal) {
        this.recognitionProcessHandler()
      } else {
        this.loading = getLoading4Cxt(this)
        this.recognitionProcessByAnalyseTextHandler().finally(() => this.loading.close())
      }
    },
    // 新增进度条动画控制方法
    startProgressAnimation(startValue, endValue, duration) {
      // 清除之前的定时器
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
      }
      const interval = setInterval(() => {
        if (this.uploadProgress < endValue) {
          this.uploadProgress += 10
        } else {
          clearInterval(interval)
        }
      }, duration / ((endValue - startValue) / 10))
      this.progressTimer = interval // 保存定时器引用
      return interval
    },

    // 构建表单数据
    buildFormData() {
      const formData = new FormData()
      formData.append('file', this.currentFile)
      formData.append('ocrTemplateUuid', 'z0z04d83tKBWSku9-64i8g')
      return formData
    },

    // 处理识别失败
    handleRecognitionFailure(error) {
      this.$message.error(this.$t('SeaExport.RecognitionFailed'))
      this.innerVisible = false
    },

    async recognitionProcessHandler() {
      this.step = 3
      // 模拟上传进度 跑到30
      this.startProgressAnimation(0, 30, 2000)

      try {
        // 构建接口参数
        const formData = this.buildFormData()

        // 调用识别接口处理
        const result = await syncOcrByTemplateUuidWithFile(formData).then((res) => {
          return res
        })

        // 处理结果
        if (result.ok && result.data) {
          let finalResult = []
          const resultArray = JSON.parse(result.data)
          // 多个结果的情况下
          if (resultArray.length > 1) {
            for (const item of resultArray) {
              if (item.text?.data && item.text?.data?.afrAddress && item.text?.data?.afrAddress.length && (item.text?.data?.afrAddress[0]?.shipperLine1 || item.text?.data?.afrAddress[0]?.consigneeLine1)) {
                finalResult.push(item)
                break
              }
            }
          } else if (resultArray.length === 1) {
            // 非多个结果
            finalResult = [...resultArray]
          }

          if (finalResult && finalResult.length) {
            // 弹窗开启再做处理
            if (this.innerVisible) {
              // 调用成功，停止轮询
              this.processResultHandler(finalResult)
              return
            } else {
              return
            }
          }
        }
        this.handleRecognitionFailure()
      } catch (error) {
        this.handleRecognitionFailure(error)
      }
    },

    async recognitionProcessByAnalyseTextHandler() {
      // 构建参数
      const data = {
        textContent: this.textContent,
        promptKey: this.promptKey
      }

      const result = await analyseText(data).then(res => {
        return res.data
      })
      this.processResultHandler(result)
    },

    // 轮询分析结果
    async pollAnalysisResults(analysisId) {
      const maxPollCount = 10
      const pollInterval = 10000
      let pollCount = 0

      // 创建新的定时器
      this.pollTimer = setInterval(async() => {
        // 如果弹窗已关闭，停止轮询
        if (!this.innerVisible || !this.pollTimer) {
          this.clearPollTimer()
          return
        }

        // 如果达到最大轮询次数，停止轮询
        if (pollCount >= maxPollCount) {
          this.clearPollTimer()
          this.handleRecognitionFailure()
          return
        }

        try {
          const result = await getAnalysis(analysisId).then(res => res.data)
          pollCount++

          if (result) {
            const jsonResult = typeof result === 'string' ? JSON.parse(result) : result
            // 调用成功，停止轮询
            this.processResultHandler(result)
          }
        } catch (error) {
          console.error('轮询请求失败:', error)
        }
      }, pollInterval)
    },
    resetData() {
      this.step = 4
      this.currentFile = null
      this.uploadProgress = 0
      this.clearPollTimer()
    },
    /**
     * 清除定时器
     */
    clearPollTimer() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    processResultHandler(result) {
      if (result) {
        if (this.multimodal) {
          // 让进度条达到100%
          this.startProgressAnimation(30, 100, 1000)
        }

        // 将结果返回处理
        this.callBack(result)
        this.innerVisible = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-recognition-dialog {
  font-size: 16px;

  ::v-deep .el-dialog {
    .el-dialog__header {
      padding: 0;
      border-bottom: none;
    }

    .el-dialog__title {
      color: #000000;
      margin-left: 27px;
      margin-top: 20px;
    }

    .el-dialog__body {
      height: 345px;
    }

    .el-dialog__footer {
      padding-top: 0;
      padding-bottom: 23px;
      border: none;
      margin-right: 15px;
    }
  }

  .upload-area {
    color: #000000;
    display: flex; /* 使用 Flexbox 布局 */
    flex-direction: column; /* 垂直排列子元素 */
    margin-left: 15px;

    .support-tip {
      margin-left: 10px;
      margin-top: 28px;
    }

    .drop-area,
    .preview-area,
    .progress-area {
      border: 1px solid #D3D3D3;
      border-radius: 6px;
      background-color: #F7F7F7;
      width: 629px;
      height: 218px;
      display: flex;
      justify-content: center; /* 水平居中 */
    }

    .drop-area {
      .drop-info {
        margin-top: 20px;
      }

      &:hover {
        border-color: #409EFF;
      }

      .drop-icon-large {
        width: 90px;
        height: 90px;
      }

      ::v-deep .el-upload-body .el-upload-dragger {
        height: 140px;
        border: none;
        background-color: #F7F7F7;
      }

      .el-upload__text {
        font-size: 16px;
        color: #000000;
      }

      .el-upload__text em {
        font-size: 16px;
      }

      .el-upload__tip {
        font-size: 16px;
        margin-top: 0;
      }
    }

    .preview-info {
      display: flex; /* 使用 Flexbox 布局 */
      flex-direction: column; /* 垂直排列子元素 */
      align-items: center; /* 垂直居中对齐 */
      margin-top: 20px;

      .preview-icon-large {
        width: 90px;
        height: 90px;
      }

      .file-name {
        margin-top: 10px;
        font-size: 16px;
      }

      .button-reupload-text {
        margin-top: 10px
      }

      ::v-deep .button-reupload-text span {
        font-size: 16px !important;
      }
    }

    .progress-info {
      display: flex; /* 使用 Flexbox 布局 */
      flex-direction: column; /* 垂直排列子元素 */
      align-items: center; /* 垂直居中对齐 */
      margin-top: 20px;

      .progress-icon-large {
        width: 90px;
        height: 90px;
      }

      .progress-tip {
        margin-top: 10px;
        color: #8C8C8C;
        font-size: 16px;
      }

      .progress-bar {
        margin-top: 10px;

        ::v-deep .el-progress-bar {
          width: 300px;
        }

        ::v-deep .el-progress-bar__outer {
          background-color: #A0D3FB;
        }
      }
    }

    .ai-recognition-warn {
      display: flex; /* 使用 Flexbox 布局 */
      align-items: center; /* 垂直居中对齐 */
      background-color: #FCF1DF;
      height: 27px;
      width: 628px;
    }

    .ai-recognition-warn img {
      margin-right: 8px;
      margin-left: 8px;
    }

    .ai-recognition-warn p {
      font-size: 14px;
      color: #F09737;
      padding-top: 5px;
      margin: 0; /* 移除默认的外边距 */
      text-align: left; /* 左对齐文本 */
    }

    .recognition-info {
      height: 320px;
      border: 1px solid #D3D3D3;
      font-size: 14px;

      ::v-deep .el-textarea__inner {
        border: none;
        resize: none;
        height: 315px !important;
      }
    }

    .button-dialog-footer {
      margin-right: 30px;
      margin-bottom: 30px;

      ::v-deep .el-button .el-button--default .el-button--medium .is-plain {
        width: 110px;
        height: 40px;
      }

    }
  }
}
</style>
